export type SupportedLanguage = 'en' | 'ar' | 'fr' | 'pt' | 'es';

export interface LanguageOption {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr' },
];

export const translations = {
  en: {
    'plugin.title': "Connect to PriceLoss",
    'plugin.loading': "Loading connection request…",
    'plugin.expired':
      "This connection request is invalid or expired. Return to the requesting client and start again.",
    'plugin.error':
      "We couldn’t complete this connection request. Return to the requesting client and try again.",
    'plugin.complete': "Returning to the requesting client…",
    'plugin.client': "Requesting client",
    'plugin.account': "You are connecting this account",
    'plugin.permissions': "Requested permissions",
    'plugin.read':
      "Read your account, watches, price history and notifications.",
    'plugin.write':
      "Manage your watches and alert settings, and verify products.",
    'plugin.events': "Receive price-drop events for your account.",
    'plugin.notice':
      "Allow only a client you trust. Denying keeps this client disconnected.",
    'plugin.submitting': "Saving your decision…",
    'plugin.approve': "Allow connection",
    'plugin.deny': "Deny connection",
    // Navigation & Header
    'nav.trackTab': 'Track Tab',
    'nav.watchlist': 'Watchlist',
    'nav.notifications': 'Notifications',
    'nav.activeQuota': '{{active}} / {{max}} Active',
    'nav.settings': 'Settings',
    'nav.logout': 'Sign Out',
    'nav.toggleTheme': 'Toggle theme',
    'nav.openSidePanel': 'Open in Side Panel',
    'sidepanel.title': 'PriceLoss Side Panel',
    'sidepanel.unsupportedPage': 'Open an ecommerce product page in your active browser tab to track.',

    // Notifications
    'notifications.title': 'Price Drop Alerts',
    'notifications.empty': 'No notifications yet',
    'notifications.emptyDesc': 'When a price drops on any item in your watchlist, you will see alerts here.',
    'notifications.markAllRead': 'Mark all as read',
    'notifications.priceDropped': 'Price dropped by',
    'notifications.savings': 'Saved',
    'notifications.was': 'Was',
    'notifications.now': 'Now',

    // Dashboard & Tracking
    'track.readyTitle': 'Ready to track products',
    'track.readyDesc': 'Navigate to any product page on a supported retailer to start price monitoring.',
    'track.button': 'Track Current Tab',
    'track.analyzing': 'Scanning product details...',
    'track.checking': 'Verifying product price...',
    'track.intervalNotice': 'Price checks run every 10–30 min with automatic tier escalation.',
    'track.unsupported': 'Open a supported store product page (Amazon, Noon, etc.) to track.',
    'track.standardTier': 'Standard Tier Account',
    'track.activeWatches': 'Active Watches',

    // Verification & Alert Setup
    'preview.verified': 'Verified',
    'preview.confidence': 'Confidence',
    'preview.alertTitle': 'Alert Trigger Settings',
    'preview.alertDesc': 'Notify me on price drop of:',
    'preview.preset.any': 'Any Drop',
    'preview.preset.5': '5% Drop',
    'preview.preset.10': '10% Drop',
    'preview.preset.15': '15% Drop',
    'preview.confirmTrack': 'Start Tracking Price',
    'preview.creating': 'Creating Price Watch...',
    'preview.alreadyTracking': 'This product is already in your watchlist.',

    // Active Watch Card
    'watch.active': 'Active',
    'watch.activelyMonitored': 'Actively Monitored',
    'watch.paused': 'Paused',
    'watch.pause': 'Pause',
    'watch.resume': 'Resume',
    'watch.delete': 'Delete',
    'watch.history': 'History',
    'watch.viewHistory': 'View History & Details',
    'watch.confirmDelete': 'Stop tracking?',
    'watch.openStore': 'Open retailer page',
    'watch.monitoredProduct': 'Monitored Product',
    'watch.checkNow': 'Check Price Now',
    'watch.checking': 'Checking...',
    'watch.checkSuccess': 'Price updated',
    'watch.checkNoChange': 'Price unchanged',
    'watch.cooldownNotice': 'Checked recently. Wait {{seconds}}s',
    'watch.checkNotFound': 'Check service is temporarily unavailable or product not found on this server',
    'watch.checkFailed': 'Failed to check price. Please try again later.',
    'watch.hostBusy': 'The store is currently busy processing requests. Please try again in a few moments.',
    'watch.listPrice': 'Original Price',
    'watch.discount': 'Discount',
    'watch.savings': 'Total Savings',

    // Watchlist
    'watchlist.title': 'Monitored Products',
    'watchlist.emptyTitle': 'No Price Watches Yet',
    'watchlist.emptyDesc': 'Navigate to any product page on Amazon, Best Buy, Target, or Shopify stores to track price drops.',
    'watchlist.filterAll': 'All ({{count}})',
    'watchlist.filterActive': 'Active ({{count}})',
    'watchlist.filterPaused': 'Paused ({{count}})',
    'watchlist.searchPlaceholder': 'Search watches by title or domain...',
    'watchlist.noMatch': 'No price watches match your active search or filter.',

    // History & Analytics
    'history.title': 'Price History & Analytics',
    'history.back': 'Back to Watchlist',
    'history.low30': '30-Day Low',
    'history.high30': '30-Day High',
    'history.latest': 'Current Price',
    'history.noData': 'No historical price points recorded yet.',
    'history.reportIssue': 'Report',

    // Report Modal
    'report.title': 'Report Extraction Issue',
    'report.actualPrice': 'Observed Price',
    'report.reason': 'Issue Category',
    'report.notes': 'Notes / Details',
    'report.submit': 'Submit Report',
    'report.cancel': 'Cancel',
    'report.success': 'Thank you! Your feedback has been queued for engine calibration.',

    // Auth
    'auth.signIn': 'Sign In to PriceLoss',
    'auth.createAccount': 'Create PriceLoss Account',
    'auth.email': 'Email Address',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.loginBtn': 'Sign In',
    'auth.registerBtn': 'Create Account',
    'auth.noAccount': "Don't have an account?",
    'auth.haveAccount': 'Already have an account?',
    'auth.registerLink': 'Create one',
    'auth.loginLink': 'Sign In',
    'auth.passwordMismatch': 'Passwords do not match.',

    // Email Banner
    'email.required': 'Verification Required',
    'email.desc': 'Please check your email to verify your account and enable email alerts.',
    'email.banner': 'Please check your email to verify your account and enable email alerts.',
    'email.resend': 'Resend email',
    'email.sent': 'Email sent!',

    // Settings
    'settings.title': 'Developer Settings',
    'settings.appearance': 'Appearance',
    'settings.themeDark': 'Dark Theme',
    'settings.themeLight': 'Light Theme',
    'settings.themeSystem': 'System Default',
    'settings.language': 'Language',
    'settings.apiUrl': 'API Base URL',
    'settings.save': 'Save Changes',
    'settings.reset': 'Reset',
    'settings.saved': 'Settings saved successfully!',

    // Cross-Merchant Comparison
    'compare.title': 'Compare Stores',
    'compare.bestPrice': 'Best Price',
    'compare.save': 'Save',
    'compare.otherStores': 'Other Stores',
    'compare.noMatches': 'No matches found on other stores yet',
    'compare.trackThisStore': 'Track this Store',
    'compare.tracked': 'Tracked',
    'compare.outOfStock': 'Out of Stock',
    'compare.marketSpread': 'Price Spread',
    'compare.searchStores': 'Search Other Stores',
    'compare.searchingStores': 'Searching stores...',
    'compare.noDiscoveredMatches': 'No matching offers found across competitor stores',

    // Custom Target Price & Alert Thresholds (Feature 029)
    'alert.targetPrice': 'Target Price',
    'alert.setTargetPrice': 'Set Target Price',
    'alert.targetPricePlaceholder': 'e.g. 250.00',
    'alert.dropPercentage': 'Price Drop %',
    'alert.customPercentage': 'Custom %',
    'alert.editRule': 'Edit Alert Rule',
    'alert.ruleUpdated': 'Alert preferences updated',
    'alert.targetAboveCurrent': 'Target price is above current price',
    'alert.invalidTargetPrice': 'Enter a valid target price greater than 0',
    'alert.targetPriceBadge': 'Target: {{price}}',

    // Watchlist Filters & Sorting (Feature 029)
    'watchlist.filterStores': 'Stores',
    'watchlist.allStores': 'All Stores',
    'watchlist.onSale': 'On Sale',
    'watchlist.sortBy': 'Sort by',
    'watchlist.sortRecent': 'Recent checks',
    'watchlist.sortSavings': 'Highest savings',
    'watchlist.sortLowestPrice': 'Lowest price',
    'watchlist.sortNewest': 'Newest added',
    'watchlist.sortOldest': 'Oldest added',
    'watchlist.clearFilters': 'Clear filters',
    'watchlist.noFilterMatches': 'No products match current filters',

    // Manual Element Picker (Feature 030)
    'picker.selectManually': 'Select Manually on Page',
    'picker.stepPrice': 'Step 1: Click the Price on page',
    'picker.stepTitle': 'Step 2: Click the Product Title',
    'picker.stepImage': 'Step 3: Click the Product Image (Optional)',
    'picker.confirmAndTrack': 'Confirm & Track',
    'picker.reselect': 'Re-select',
    'picker.cancel': 'Cancel',
    'picker.skipStep': 'Skip',
    'picker.selectedPrice': 'Selected Price',
    'picker.selectedTitle': 'Selected Title',
    'picker.correctElement': 'Correct Element / Re-pick',
    'picker.saving': 'Saving...',
    'picker.errorPriceRequired': 'Please select a valid price element',
    'picker.connectionError': 'Could not connect to page inspector. Please refresh the page and try again.',
    'picker.refreshPage': 'Refresh Page',

    // Web Platform Enhancements (Feature 032)
    'track.pastePlaceholder': 'Paste any product URL (Amazon, Noon, Jarir, eBay...)',
    'track.trackPrice': 'Track Price',
    'track.tracking': 'Tracking...',
    'dashboard.searchPlaceholder': 'Search products or stores...',
    'dashboard.filterAll': 'All',
    'dashboard.filterActive': 'Active',
    'dashboard.filterPaused': 'Paused',
    'dashboard.emptyTitle': 'No tracked products yet',
    'dashboard.emptyDesc': 'Paste a product URL above to start monitoring prices and receiving drop alerts.',
    'dashboard.cardCount': '{{count}} products',
    'card.pauseTracking': 'Pause tracking',
    'card.resumeTracking': 'Resume tracking',
    'card.viewDetails': 'View product details and history',
    'card.visitStore': 'Visit webstore',
    'card.delete': 'Delete watch',
    'card.statusActive': 'ACTIVE',
    'card.statusPaused': 'PAUSED',
    'card.currentPrice': 'Current Price',
    'card.listPrice': 'List Price',
    'card.save': 'Save',
    'dialog.deleteTitle': 'Delete Price Watch',
    'dialog.deleteDesc': 'Are you sure you want to stop tracking "{{title}}"? Historical price observations for this item will no longer be tracked.',
    'dialog.cancel': 'Cancel',
    'dialog.confirmDelete': 'Confirm Delete',
    'modal.productDetails': 'Product Details',
    'modal.priceHistory': 'Price History & Analytics',
    'modal.alertSettings': 'Alert Trigger Settings',
    'modal.range30d': '30 Days',
    'modal.range90d': '90 Days',
    'modal.rangeAll': 'All History',
    'modal.targetPrice': 'Target Price',
    'modal.dropPercentage': 'Price Drop %',
    'modal.saveAlert': 'Save Alert Settings',
    'modal.saving': 'Saving...',
    'modal.saveSuccess': 'Alert rule updated successfully',
    'modal.anyDrop': 'Any Drop',
    'modal.drop5': '5% Drop',
    'modal.drop10': '10% Drop',
    'modal.drop15': '15% Drop',
    'modal.drop20': '20% Drop',
    'modal.drop25': '25% Drop',
    'modal.customDrop': 'Custom %',
    'modal.storeLink': 'Open Webstore',
    'modal.currentPrice': 'Current Price',
    'modal.originalPrice': 'List Price',
    'modal.savings': 'Savings',
    'modal.noHistory': 'No historical price data recorded yet.',
    'email.verifyNotice': 'Please verify your email address to ensure price drop notifications reach your inbox.',

    // Browser Sync (Feature 035)
    'sync.justNow': 'Just now',
    'sync.syncedFromBrowser': 'Price updated from browser!',
    'sync.cooldownActive': 'Already synchronized within the past hour.',
    'sync.syncFromBrowser': 'Sync from Browser Tab',
    'sync.syncing': 'Syncing...',

    // User Packages & Retention (Feature 063)
    'packages.title': 'Your Package & Watch Allowance',
    'packages.currentPackage': 'Current Plan',
    'packages.activeUsage': '{{active}} of {{limit}} active watches used',
    'packages.effectiveLimit': 'Effective Limit',
    'packages.activationLimit': 'Package Limit',
    'packages.complimentary': 'Complimentary Grant',
    'packages.complimentaryDesc': 'Your package is provided complimentary. Paid checkout is coming soon.',
    'packages.paymentsComingSoon': 'Online payments coming soon',
    'packages.pendingDowngradeTitle': 'Scheduled Plan Downgrade',
    'packages.graceDeadline': 'Grace period ends: {{deadline}}',
    'packages.graceWarning': 'Your active watches exceed the {{target}} limit. Select which watches to retain below before the grace period ends.',
    'packages.retentionTitle': 'Select Watches to Retain',
    'packages.retentionSubtitle': 'Choose up to {{limit}} active watches to keep running after the downgrade.',
    'packages.saveRetention': 'Save Retained Watches',
    'packages.savingRetention': 'Saving selection...',
    'packages.savedSuccess': 'Watch retention selection saved.',
    'packages.saveError': 'Failed to save retention selection.',
    'packages.oldestFirstNotice': 'Default: oldest watches retained first if no custom selection is made.',
    'packages.retainedCount': '{{selected}} / {{limit}} selected',
    'packages.unretainedPausedNotice': 'Paused watches preserve full historical price data.',
    'packages.loading': 'Loading package details...',
    'packages.unavailable': 'Package details currently unavailable.',
    'packages.retry': 'Retry',
    'packages.tierFree': 'FREE',
    'packages.tierPlus': 'PLUS',
    'packages.tierPro': 'PRO',
    'packages.tierSpecial': 'Special Package',
    'packages.tierPremium': 'Premium',
    'packages.priceMonthly': '{{price}}/mo',
    'packages.quotaExceededAlert': 'Active watch limit reached. Pause or delete another watch to activate this one.',
    'packages.watchesAllowance': '{{limit}} watches',
    'packages.plannedPricing': 'Available Plans',
  },

  ar: {
    'plugin.title': "الاتصال بـ PriceLoss",
    'plugin.loading': "جارٍ تحميل طلب الاتصال…",
    'plugin.expired':
      "طلب الاتصال غير صالح أو منتهي الصلاحية. ارجع إلى التطبيق الذي طلب الاتصال وابدأ من جديد.",
    'plugin.error':
      "تعذّر إكمال طلب الاتصال. ارجع إلى التطبيق الذي طلب الاتصال وحاول مرة أخرى.",
    'plugin.complete': "جارٍ الرجوع إلى التطبيق الذي طلب الاتصال…",
    'plugin.client': "التطبيق الذي يطلب الاتصال",
    'plugin.account': "أنت تربط هذا الحساب",
    'plugin.permissions': "الأذونات المطلوبة",
    'plugin.read': "قراءة حسابك والمنتجات المتابَعة وسجل الأسعار والإشعارات.",
    'plugin.write':
      "إدارة المنتجات المتابَعة وإعدادات التنبيه والتحقق من المنتجات.",
    'plugin.events': "تلقي أحداث انخفاض الأسعار لحسابك.",
    'plugin.notice': "اسمح فقط لتطبيق تثق به. الرفض يبقي هذا التطبيق غير متصل.",
    'plugin.submitting': "جارٍ حفظ قرارك…",
    'plugin.approve': "السماح بالاتصال",
    'plugin.deny': "رفض الاتصال",
    // Navigation & Header
    'nav.trackTab': 'متابعة الصفحة',
    'nav.watchlist': 'قائمة المتابعة',
    'nav.notifications': 'الإشعارات',
    'nav.activeQuota': '{{active}} / {{max}} نشط',
    'nav.settings': 'الإعدادات',
    'nav.logout': 'تسجيل الخروج',
    'nav.toggleTheme': 'تبديل المظهر',
    'nav.openSidePanel': 'فتح في اللوحة الجانبية',
    'sidepanel.title': 'لوحة PriceLoss الجانبية',
    'sidepanel.unsupportedPage': 'افتح صفحة منتج تجاري في علامة التبويب النشطة لتتبعها.',

    // Notifications
    'notifications.title': 'تنبيهات انخفاض الأسعار',
    'notifications.empty': 'لا توجد إشعارات حتى الآن',
    'notifications.emptyDesc': 'عند انخفاض سعر أي منتج في قائمة المتابعة، ستظهر التنبيهات هنا.',
    'notifications.markAllRead': 'تحديد الكل كمقروء',
    'notifications.priceDropped': 'انخفض السعر بنسبة',
    'notifications.savings': 'التوفير',
    'notifications.was': 'كان',
    'notifications.now': 'الآن',

    // Dashboard & Tracking
    'track.readyTitle': 'جاهز لمتابعة المنتجات',
    'track.readyDesc': 'انتقل إلى صفحة أي منتج في المتاجر المدعومة لبدء مراقبة الأسعار.',
    'track.button': 'متابعة الصفحة الحالية',
    'track.analyzing': 'جاري فحص بيانات المنتج...',
    'track.checking': 'جاري التحقق من سعر المنتج...',
    'track.intervalNotice': 'يتم فحص الأسعار تلقائياً كل 10–30 دقيقة.',
    'track.unsupported': 'يرجى فتح صفحة منتج في متجر مدعوم (أمازون، نون، إلخ).',
    'track.standardTier': 'حساب قياسي',
    'track.activeWatches': 'المنتجات النشطة',

    // Verification & Alert Setup
    'preview.verified': 'مؤكد',
    'preview.confidence': 'مستوى الدقة',
    'preview.alertTitle': 'إعدادات تنبيهات الأسعار',
    'preview.alertDesc': 'تنبيه عند انخفاض السعر بمقدار:',
    'preview.preset.any': 'أي انخفاض',
    'preview.preset.5': 'انخفاض 5%',
    'preview.preset.10': 'انخفاض 10%',
    'preview.preset.15': 'انخفاض 15%',
    'preview.confirmTrack': 'بدء متابعة السعر',
    'preview.creating': 'جاري إنشاء المتابعة...',
    'preview.alreadyTracking': 'هذا المنتج مضاف بالفعل في قائمة المتابعة.',

    // Active Watch Card
    'watch.active': 'نشط',
    'watch.activelyMonitored': 'مراقب بنشاط',
    'watch.paused': 'متوقف مؤقتاً',
    'watch.pause': 'إيقاف مؤقت',
    'watch.resume': 'استئناف',
    'watch.delete': 'حذف',
    'watch.history': 'السجل',
    'watch.viewHistory': 'عرض السجل والتفاصيل',
    'watch.confirmDelete': 'إيقاف المتابعة؟',
    'watch.openStore': 'فتح صفحة المتجر',
    'watch.monitoredProduct': 'منتج مراقب',
    'watch.checkNow': 'فحص السعر الآن',
    'watch.checking': 'جاري الفحص...',
    'watch.checkSuccess': 'تم تحديث السعر',
    'watch.checkNoChange': 'السعر لم يتغير',
    'watch.cooldownNotice': 'تم الفحص مؤخراً. انتظر {{seconds}} ثانية',
    'watch.checkNotFound': 'خدمة فحص السعر غير متوفرة على هذا الخادم أو لم يتم العثور على المنتج',
    'watch.checkFailed': 'تعذر فحص السعر. يرجى المحاولة لاحقاً.',
    'watch.hostBusy': 'المتجر مشغول حالياً بمعالجة الطلبات. يرجى المحاولة بعد قليل.',
    'watch.listPrice': 'السعر الأصلي',
    'watch.discount': 'خصم',
    'watch.savings': 'إجمالي التوفير',

    // Watchlist
    'watchlist.title': 'المنتجات المراقبة',
    'watchlist.emptyTitle': 'لا توجد منتجات متابعة بعد',
    'watchlist.emptyDesc': 'ابدأ بمتابعة المنتجات من متصفحك لتصلك تنبيهات فورية عند انخفاض أسعارها.',
    'watchlist.filterAll': 'الكل ({{count}})',
    'watchlist.filterActive': 'النشطة ({{count}})',
    'watchlist.filterPaused': 'المتوقفة ({{count}})',
    'watchlist.searchPlaceholder': 'بحث في المنتجات المتابعة...',
    'watchlist.noMatch': 'لا توجد نتائج مطابقة للبحث أو الفلتر.',

    // History & Analytics
    'history.title': 'سجل وتحليلات الأسعار',
    'history.back': 'العودة لقائمة المتابعة',
    'history.low30': 'أدنى سعر (30 يوم)',
    'history.high30': 'أعلى سعر (30 يوم)',
    'history.latest': 'السعر الحالي',
    'history.noData': 'لا توجد بيانات أسعار مسجلة حتى الآن.',
    'history.reportIssue': 'إبلاغ',

    // Report Modal
    'report.title': 'الإبلاغ عن مشكلة في الاستخراج',
    'report.actualPrice': 'السعر الملاحظ',
    'report.reason': 'فئة المشكلة',
    'report.notes': 'ملاحظات وتفاصيل',
    'report.submit': 'إرسال التقرير',
    'report.cancel': 'إلغاء',
    'report.success': 'شكراً لك! تم استلام ملاحظاتك بنجاح.',

    // Auth
    'auth.signIn': 'تسجيل الدخول إلى PriceLoss',
    'auth.createAccount': 'إنشاء حساب PriceLoss',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.confirmPassword': 'تأكيد كلمة المرور',
    'auth.loginBtn': 'دخول',
    'auth.registerBtn': 'إنشاء حساب',
    'auth.noAccount': 'ليس لديك حساب؟',
    'auth.haveAccount': 'لديك حساب بالفعل؟',
    'auth.registerLink': 'إنشاء حساب',
    'auth.loginLink': 'تسجيل الدخول',
    'auth.passwordMismatch': 'كلمات المرور غير متطابقة.',

    // Email Banner
    'email.required': 'التأكيد مطلوب',
    'email.desc': 'يرجى مراجعة بريدك الإلكتروني لتأكيد الحساب وتفعيل التنبيهات.',
    'email.banner': 'يرجى مراجعة بريدك الإلكتروني لتأكيد الحساب وتفعيل التنبيهات.',
    'email.resend': 'إعادة الإرسال',
    'email.sent': 'تم إرسال البريد!',

    // Settings
    'settings.title': 'إعدادات المطور',
    'settings.appearance': 'المظهر',
    'settings.themeDark': 'الوضع الداكن',
    'settings.themeLight': 'الوضع الفاتح',
    'settings.themeSystem': 'تلقائي حسب النظام',
    'settings.language': 'اللغة',
    'settings.apiUrl': 'عنوان خادم API',
    'settings.save': 'حفظ التغييرات',
    'settings.reset': 'إعادة ضبط',
    'settings.saved': 'تم حفظ الإعدادات بنجاح!',

    // Cross-Merchant Comparison
    'compare.title': 'مقارنة المتاجر',
    'compare.bestPrice': 'أفضل سعر',
    'compare.save': 'وفر',
    'compare.otherStores': 'متاجر أخرى',
    'compare.noMatches': 'لم يتم العثور على مطابقات في متاجر أخرى بعد',
    'compare.trackThisStore': 'تتبع هذا المتجر',
    'compare.tracked': 'تم التتبع',
    'compare.outOfStock': 'غير متوفر',
    'compare.marketSpread': 'فارق الأسعار',
    'compare.searchStores': 'البحث في متاجر أخرى',
    'compare.searchingStores': 'جارٍ البحث في المتاجر...',
    'compare.noDiscoveredMatches': 'لم يتم العثور على عروض مطابقة في المتاجر المنافسة',

    // Custom Target Price & Alert Thresholds (Feature 029)
    'alert.targetPrice': 'السعر المستهدف',
    'alert.setTargetPrice': 'تحديد السعر المستهدف',
    'alert.targetPricePlaceholder': 'مثال: 250.00',
    'alert.dropPercentage': 'نسبة انخفاض السعر',
    'alert.customPercentage': 'نسبة مخصصة %',
    'alert.editRule': 'تعديل التنبيه',
    'alert.ruleUpdated': 'تم تحديث تفضيلات التنبيه',
    'alert.targetAboveCurrent': 'السعر المستهدف أعلى من السعر الحالي',
    'alert.invalidTargetPrice': 'أدخل سعراً مستهدفاً صالحاً أكبر من 0',
    'alert.targetPriceBadge': 'المستهدف: {{price}}',

    // Watchlist Filters & Sorting (Feature 029)
    'watchlist.filterStores': 'المتاجر',
    'watchlist.allStores': 'جميع المتاجر',
    'watchlist.onSale': 'تخفيضات فقط',
    'watchlist.sortBy': 'ترتيب حسب',
    'watchlist.sortRecent': 'آخر فحص',
    'watchlist.sortSavings': 'أعلى توفير',
    'watchlist.sortLowestPrice': 'أقل سعر',
    'watchlist.sortNewest': 'الأحدث إضافة',
    'watchlist.sortOldest': 'الأقدم إضافة',
    'watchlist.clearFilters': 'إعادة ضبط الفلاتر',
    'watchlist.noFilterMatches': 'لا توجد منتجات تطابق الفلاتر الحالية',

    // Manual Element Picker (Feature 030)
    'picker.selectManually': 'تحديد يدوي في الصفحة',
    'picker.stepPrice': 'الخطوة 1: انقر على السعر في الصفحة',
    'picker.stepTitle': 'الخطوة 2: انقر على عنوان المنتج',
    'picker.stepImage': 'الخطوة 3: انقر على صورة المنتج (اختياري)',
    'picker.confirmAndTrack': 'تأكيد ومتابعة',
    'picker.reselect': 'إعادة التحديد',
    'picker.cancel': 'إلغاء',
    'picker.skipStep': 'تخطي',
    'picker.selectedPrice': 'السعر المحدد',
    'picker.selectedTitle': 'العنوان المحدد',
    'picker.correctElement': 'تصحيح العنصر / إعادة التحديد',
    'picker.saving': 'جارٍ الحفظ...',
    'picker.errorPriceRequired': 'يرجى تحديد عنصر سعر صالح',
    'picker.connectionError': 'تعذر الاتصال بمفتش الصفحة. يرجى إعادة تحميل الصفحة والمحاولة مرة أخرى.',
    'picker.refreshPage': 'إعادة تحميل الصفحة',

    // Web Platform Enhancements (Feature 032)
    'track.pastePlaceholder': 'الصق رابط أي منتج (أمازون، نون، جرير، إيباي...)',
    'track.trackPrice': 'تتبع السعر',
    'track.tracking': 'جارٍ التتبع...',
    'dashboard.searchPlaceholder': 'ابحث في المنتجات أو المتاجر...',
    'dashboard.filterAll': 'الكل',
    'dashboard.filterActive': 'النشطة',
    'dashboard.filterPaused': 'المتوقفة',
    'dashboard.emptyTitle': 'لا توجد منتجات متبعة حتى الآن',
    'dashboard.emptyDesc': 'الصق رابط المنتج أعلاه لبدء مراقبة الأسعار وتلقي تنبيهات الانخفاض.',
    'dashboard.cardCount': '{{count}} منتجات',
    'card.pauseTracking': 'إيقاف التتبع مؤقتاً',
    'card.resumeTracking': 'استئناف التتبع',
    'card.viewDetails': 'عرض تفاصيل المنتج وسجل الأسعار',
    'card.visitStore': 'زيارة المتجر',
    'card.delete': 'حذف التتبع',
    'card.statusActive': 'نشط',
    'card.statusPaused': 'متوقف',
    'card.currentPrice': 'السعر الحالي',
    'card.listPrice': 'السعر الأصلي',
    'card.save': 'توفير',
    'dialog.deleteTitle': 'حذف تتبع المنتج',
    'dialog.deleteDesc': 'هل أنت متأكد من رغبتك في إيقاف تتبع "{{title}}"? لن يتم تسجيل أي انخفاضات مستقبلية في الأسعار لهذا المنتج.',
    'dialog.cancel': 'إلغاء',
    'dialog.confirmDelete': 'حذف التتبع',
    'modal.productDetails': 'تفاصيل المنتج',
    'modal.priceHistory': 'سجل الأسعار والتحليلات',
    'modal.alertSettings': 'إعدادات تنبيه السعر',
    'modal.range30d': '30 يوماً',
    'modal.range90d': '90 يوماً',
    'modal.rangeAll': 'كامل السجل',
    'modal.targetPrice': 'السعر المستهدف',
    'modal.dropPercentage': 'نسبة انخفاض السعر',
    'modal.saveAlert': 'حفظ إعدادات التنبيه',
    'modal.saving': 'جارٍ الحفظ...',
    'modal.saveSuccess': 'تم تحديث تفضيلات التنبيه بنجاح',
    'modal.anyDrop': 'أي انخفاض',
    'modal.drop5': 'انخفاض 5%',
    'modal.drop10': 'انخفاض 10%',
    'modal.drop15': 'انخفاض 15%',
    'modal.drop20': 'انخفاض 20%',
    'modal.drop25': 'انخفاض 25%',
    'modal.customDrop': 'نسبة مخصصة',
    'modal.storeLink': 'فتح صفحة المتجر',
    'modal.currentPrice': 'السعر الحالي',
    'modal.originalPrice': 'السعر الأصلي',
    'modal.savings': 'مبلغ التوفير',
    'modal.noHistory': 'لا توجد بيانات تاريخية مسجلة للأسعار حتى الآن.',
    'email.verifyNotice': 'يرجى تأكيد بريدك الإلكتروني لضمان وصول تنبيهات انخفاض الأسعار إلى صندوق الوارد.',

    // Browser Sync (Feature 035)
    'sync.justNow': 'الآن',
    'sync.syncedFromBrowser': 'تم تحديث السعر من المتصفح!',
    'sync.cooldownActive': 'تمت المزامنة بالفعل خلال الساعة الماضية.',
    'sync.syncFromBrowser': 'مزامنة من تبويب المتصفح',
    'sync.syncing': 'جارٍ المزامنة...',

    // User Packages & Retention (Feature 063)
    'packages.title': 'باقتك وحصة المتابعة',
    'packages.currentPackage': 'الخطة الحالية',
    'packages.activeUsage': 'تم استخدام {{active}} من {{limit}} متابعة نشطة',
    'packages.effectiveLimit': 'الحد الفعلي',
    'packages.activationLimit': 'حد الباقة',
    'packages.complimentary': 'منحة مجانية',
    'packages.complimentaryDesc': 'تم منح باقتك مجاناً. سيتم تفعيل الدفع الإلكتروني قريباً.',
    'packages.paymentsComingSoon': 'المدفوعات الإلكترونية قادمة قريباً',
    'packages.pendingDowngradeTitle': 'تخفيض مجدول للخطة',
    'packages.graceDeadline': 'تنتهي فترة السماح: {{deadline}}',
    'packages.graceWarning': 'المتابعات النشطة تتجاوز حد باقة {{target}}. حدد المتابعات التي تريد استمرارها أدناه قبل انتهاء المهلة.',
    'packages.retentionTitle': 'تحديد المتابعات المستمرة',
    'packages.retentionSubtitle': 'اختر ما يصل إلى {{limit}} متابعة نشطة للاستمرار بعد التخفيض.',
    'packages.saveRetention': 'حفظ المتابعات المحددة',
    'packages.savingRetention': 'جارٍ حفظ التحديد...',
    'packages.savedSuccess': 'تم حفظ تحديد المتابعات المستمرة بنجاح.',
    'packages.saveError': 'فشل حفظ التحديد.',
    'packages.oldestFirstNotice': 'افتراضياً: يتم الإبقاء على أقدم المتابعات أولاً في حال عدم التحديد اليدوي.',
    'packages.retainedCount': 'تم تحديد {{selected}} من {{limit}}',
    'packages.unretainedPausedNotice': 'المتابعات المتوقفة تحتفظ بكافة بيانات الأسعار التاريخية.',
    'packages.loading': 'جارٍ تحميل تفاصيل الباقة...',
    'packages.unavailable': 'تفاصيل الباقة غير متوفرة حالياً.',
    'packages.retry': 'إعادة المحاولة',
    'packages.tierFree': 'مجانية',
    'packages.tierPlus': 'بلس',
    'packages.tierPro': 'برو',
    'packages.tierSpecial': 'باقة خاصة',
    'packages.tierPremium': 'بريميوم',
    'packages.priceMonthly': '{{price}}/شهر',
    'packages.quotaExceededAlert': 'تم الوصول إلى الحد الأقصى للمتابعات النشطة. أوقف أو احذف متابعة أخرى لتفعيل هذه.',
    'packages.watchesAllowance': '{{limit}} متابعة',
    'packages.plannedPricing': 'الباقات المتاحة',
  },

  fr: {
    'plugin.title': "Connecter à PriceLoss",
    'plugin.loading': "Chargement de la demande de connexion…",
    'plugin.expired': "Cette demande de connexion est invalide ou a expiré. Revenez au client demandeur et recommencez.",
    'plugin.error': "Impossible de terminer cette demande de connexion. Revenez au client demandeur et réessayez.",
    'plugin.complete': "Retour au client demandeur…",
    'plugin.client': "Client demandeur",
    'plugin.account': "Vous connectez ce compte",
    'plugin.permissions': "Autorisations demandées",
    'plugin.read': "Lire votre compte, vos suivis, l’historique des prix et les notifications.",
    'plugin.write': "Gérer vos suivis et alertes, et vérifier des produits.",
    'plugin.events': "Recevoir les événements de baisse de prix de votre compte.",
    'plugin.notice': "Autorisez uniquement un client de confiance. Refuser laisse ce client déconnecté.",
    'plugin.submitting': "Enregistrement de votre décision…",
    'plugin.approve': "Autoriser la connexion",
    'plugin.deny': "Refuser la connexion",
    // Navigation & Header
    'nav.trackTab': 'Suivre l’onglet',
    'nav.watchlist': 'Mes alertes',
    'nav.notifications': 'Notifications',
    'nav.activeQuota': '{{active}} / {{max}} Actifs',
    'nav.settings': 'Paramètres',
    'nav.logout': 'Se déconnecter',
    'nav.toggleTheme': 'Changer de thème',
    'nav.openSidePanel': 'Ouvrir dans le panneau latéral',
    'sidepanel.title': 'Panneau latéral PriceLoss',
    'sidepanel.unsupportedPage': 'Ouvrez une page de produit e-commerce dans votre onglet actif pour suivre.',

    // Notifications
    'notifications.title': 'Alertes de baisse de prix',
    'notifications.empty': 'Aucune notification pour le moment',
    'notifications.emptyDesc': 'Lorsqu\'un prix baisse sur un article suivi, vous recevrez une alerte ici.',
    'notifications.markAllRead': 'Tout marquer comme lu',
    'notifications.priceDropped': 'Prix en baisse de',
    'notifications.savings': 'Économie',
    'notifications.was': 'Avant',
    'notifications.now': 'Maintenant',

    // Dashboard & Tracking
    'track.readyTitle': 'Prêt à suivre des produits',
    'track.readyDesc': 'Accédez à une page produit chez un marchand compatible pour surveiller son prix.',
    'track.button': 'Suivre l’onglet actif',
    'track.analyzing': 'Analyse du produit en cours...',
    'track.checking': 'Vérification du prix...',
    'track.intervalNotice': 'Vérification automatique des prix toutes les 10 à 30 minutes.',
    'track.unsupported': 'Ouvrez une page produit compatible (Amazon, Noon, etc.) pour commencer.',
    'track.standardTier': 'Compte Standard',
    'track.activeWatches': 'Alertes actives',

    // Verification & Alert Setup
    'preview.verified': 'Vérifié',
    'preview.confidence': 'Fiabilité',
    'preview.alertTitle': 'Paramètres de l’alerte',
    'preview.alertDesc': 'M’alerter en cas de baisse de :',
    'preview.preset.any': 'Toute baisse',
    'preview.preset.5': 'Baisse de 5%',
    'preview.preset.10': 'Baisse de 10%',
    'preview.preset.15': 'Baisse de 15%',
    'preview.confirmTrack': 'Commencer le suivi',
    'preview.creating': 'Création de l’alerte...',
    'preview.alreadyTracking': 'Ce produit est déjà dans vos alertes.',

    // Active Watch Card
    'watch.active': 'Actif',
    'watch.activelyMonitored': 'Surveillé',
    'watch.paused': 'En pause',
    'watch.pause': 'Suspendre',
    'watch.resume': 'Reprendre',
    'watch.delete': 'Supprimer',
    'watch.history': 'Historique',
    'watch.viewHistory': 'Voir l’historique et les détails',
    'watch.confirmDelete': 'Arrêter le suivi ?',
    'watch.openStore': 'Voir sur la boutique',
    'watch.monitoredProduct': 'Produit surveillé',
    'watch.checkNow': 'Vérifier le prix maintenant',
    'watch.checking': 'Vérification...',
    'watch.checkSuccess': 'Prix mis à jour',
    'watch.checkNoChange': 'Prix inchangé',
    'watch.cooldownNotice': 'Vérifié récemment. Attendez {{seconds}}s',
    'watch.checkNotFound': 'Service de vérification indisponible sur ce serveur ou produit introuvable',
    'watch.checkFailed': 'Échec de la vérification du prix. Veuillez réessayer plus tard.',
    'watch.hostBusy': 'La boutique est actuellement occupée. Veuillez réessayer dans quelques instants.',
    'watch.listPrice': 'Prix d’origine',
    'watch.discount': 'Remise',
    'watch.savings': 'Économie totale',

    // Watchlist
    'watchlist.title': 'Produits surveillés',
    'watchlist.emptyTitle': 'Aucune alerte pour l’instant',
    'watchlist.emptyDesc': 'Suivez des produits pour recevoir des alertes par email dès que le prix baisse.',
    'watchlist.filterAll': 'Tous ({{count}})',
    'watchlist.filterActive': 'Actifs ({{count}})',
    'watchlist.filterPaused': 'En pause ({{count}})',
    'watchlist.searchPlaceholder': 'Rechercher un produit...',
    'watchlist.noMatch': 'Aucun produit ne correspond à votre recherche.',

    // History & Analytics
    'history.title': 'Historique des prix',
    'history.back': 'Retour aux alertes',
    'history.low30': 'Plus bas (30j)',
    'history.high30': 'Plus haut (30j)',
    'history.latest': 'Prix actuel',
    'history.noData': 'Aucun historique de prix disponible.',
    'history.reportIssue': 'Signaler',

    // Report Modal
    'report.title': 'Signaler un problème d’extraction',
    'report.actualPrice': 'Prix constaté',
    'report.reason': 'Catégorie du problème',
    'report.notes': 'Remarques / Détails',
    'report.submit': 'Envoyer le rapport',
    'report.cancel': 'Annuler',
    'report.success': 'Merci ! Vos remarques ont été transmises à notre équipe.',

    // Auth
    'auth.signIn': 'Connexion à PriceLoss',
    'auth.createAccount': 'Créer un compte PriceLoss',
    'auth.email': 'Adresse email',
    'auth.password': 'Mot de passe',
    'auth.confirmPassword': 'Confirmer le mot de passe',
    'auth.loginBtn': 'Se connecter',
    'auth.registerBtn': 'Créer un compte',
    'auth.noAccount': 'Pas encore de compte ?',
    'auth.haveAccount': 'Vous avez déjà un compte ?',
    'auth.registerLink': 'Créer un compte',
    'auth.loginLink': 'Se connecter',
    'auth.passwordMismatch': 'Les mots de passe ne correspondent pas.',

    // Email Banner
    'email.required': 'Vérification requise',
    'email.desc': 'Veuillez vérifier votre email pour activer les alertes.',
    'email.banner': 'Veuillez vérifier votre email pour activer les alertes.',
    'email.resend': 'Renvoyer l’email',
    'email.sent': 'Email envoyé !',

    // Settings
    'settings.title': 'Paramètres Développeur',
    'settings.appearance': 'Apparence',
    'settings.themeDark': 'Thème sombre',
    'settings.themeLight': 'Thème clair',
    'settings.themeSystem': 'Système',
    'settings.language': 'Langue',
    'settings.apiUrl': 'URL de l’API',
    'settings.save': 'Enregistrer',
    'settings.reset': 'Réinitialiser',
    'settings.saved': 'Paramètres enregistrés avec succès !',

    // Cross-Merchant Comparison
    'compare.title': 'Comparer les magasins',
    'compare.bestPrice': 'Meilleur prix',
    'compare.save': 'Économisez',
    'compare.otherStores': 'Autres magasins',
    'compare.noMatches': 'Aucune correspondance trouvée sur d’autres magasins pour le moment',
    'compare.trackThisStore': 'Suivre ce magasin',
    'compare.tracked': 'Suivi',
    'compare.outOfStock': 'Rupture de stock',
    'compare.marketSpread': 'Écart de prix',
    'compare.searchStores': "Rechercher dans d'autres magasins",
    'compare.searchingStores': 'Recherche dans les magasins en cours...',
    'compare.noDiscoveredMatches': 'Aucune offre correspondante trouvée sur les magasins concurrents',

    // Custom Target Price & Alert Thresholds (Feature 029)
    'alert.targetPrice': 'Prix cible',
    'alert.setTargetPrice': 'Définir un prix cible',
    'alert.targetPricePlaceholder': 'ex. 250.00',
    'alert.dropPercentage': '% de baisse de prix',
    'alert.customPercentage': '% personnalisé',
    'alert.editRule': 'Modifier l’alerte',
    'alert.ruleUpdated': 'Préférences d’alerte mises à jour',
    'alert.targetAboveCurrent': 'Le prix cible est supérieur au prix actuel',
    'alert.invalidTargetPrice': 'Entrez un prix cible valide supérieur à 0',
    'alert.targetPriceBadge': 'Cible : {{price}}',

    // Watchlist Filters & Sorting (Feature 029)
    'watchlist.filterStores': 'Boutiques',
    'watchlist.allStores': 'Toutes les boutiques',
    'watchlist.onSale': 'En promotion',
    'watchlist.sortBy': 'Trier par',
    'watchlist.sortRecent': 'Vérifications récentes',
    'watchlist.sortSavings': 'Meilleures économies',
    'watchlist.sortLowestPrice': 'Prix le plus bas',
    'watchlist.sortNewest': 'Plus récents',
    'watchlist.sortOldest': 'Plus anciens',
    'watchlist.clearFilters': 'Effacer les filtres',
    'watchlist.noFilterMatches': 'Aucun produit ne correspond aux filtres actuels',

    // Manual Element Picker (Feature 030)
    'picker.selectManually': 'Sélectionner manuellement sur la page',
    'picker.stepPrice': 'Étape 1 : Cliquez sur le prix sur la page',
    'picker.stepTitle': 'Étape 2 : Cliquez sur le titre du produit',
    'picker.stepImage': 'Étape 3 : Cliquez sur l’image du produit (Facultatif)',
    'picker.confirmAndTrack': 'Confirmer et suivre',
    'picker.reselect': 'Re-sélectionner',
    'picker.cancel': 'Annuler',
    'picker.skipStep': 'Passer',
    'picker.selectedPrice': 'Prix sélectionné',
    'picker.selectedTitle': 'Titre sélectionné',
    'picker.correctElement': 'Corriger l’élément / Re-sélectionner',
    'picker.saving': 'Enregistrement...',
    'picker.errorPriceRequired': 'Veuillez sélectionner un élément de prix valide',
    'picker.connectionError': 'Impossible de se connecter à l\'inspecteur de page. Veuillez actualiser la page et réessayer.',
    'picker.refreshPage': 'Actualiser la page',

    // Web Platform Enhancements (Feature 032)
    'track.pastePlaceholder': 'Collez l\'URL d\'un produit (Amazon, Fnac, eBay...)',
    'track.trackPrice': 'Suivre le prix',
    'track.tracking': 'Suivi en cours...',
    'dashboard.searchPlaceholder': 'Rechercher des produits ou boutiques...',
    'dashboard.filterAll': 'Tous',
    'dashboard.filterActive': 'Actifs',
    'dashboard.filterPaused': 'En pause',
    'dashboard.emptyTitle': 'Aucun produit suivi pour l\'instant',
    'dashboard.emptyDesc': 'Collez l\'URL d\'un produit ci-dessus pour surveiller son prix et recevoir des alertes.',
    'dashboard.cardCount': '{{count}} produits',
    'card.pauseTracking': 'Suspendre le suivi',
    'card.resumeTracking': 'Reprendre le suivi',
    'card.viewDetails': 'Voir les détails et l\'historique',
    'card.visitStore': 'Visiter la boutique',
    'card.delete': 'Supprimer le suivi',
    'card.statusActive': 'ACTIF',
    'card.statusPaused': 'EN PAUSE',
    'card.currentPrice': 'Prix actuel',
    'card.listPrice': 'Prix catalogue',
    'card.save': 'Économie',
    'dialog.deleteTitle': 'Supprimer le suivi',
    'dialog.deleteDesc': 'Êtes-vous sûr de vouloir arrêter le suivi de "{{title}}" ? Les données d\'historique ne seront plus actualisées.',
    'dialog.cancel': 'Annuler',
    'dialog.confirmDelete': 'Supprimer',
    'modal.productDetails': 'Détails du produit',
    'modal.priceHistory': 'Historique et analyse des prix',
    'modal.alertSettings': 'Paramètres d\'alerte',
    'modal.range30d': '30 jours',
    'modal.range90d': '90 jours',
    'modal.rangeAll': 'Tout l\'historique',
    'modal.targetPrice': 'Prix cible',
    'modal.dropPercentage': '% de baisse',
    'modal.saveAlert': 'Enregistrer l\'alerte',
    'modal.saving': 'Enregistrement...',
    'modal.saveSuccess': 'Paramètres d\'alerte mis à jour',
    'modal.anyDrop': 'Toute baisse',
    'modal.drop5': 'Baisse de 5%',
    'modal.drop10': 'Baisse de 10%',
    'modal.drop15': 'Baisse de 15%',
    'modal.drop20': 'Baisse de 20%',
    'modal.drop25': 'Baisse de 25%',
    'modal.customDrop': '% personnalisé',
    'modal.storeLink': 'Ouvrir la boutique',
    'modal.currentPrice': 'Prix actuel',
    'modal.originalPrice': 'Prix d\'origine',
    'modal.savings': 'Économie réalisée',
    'modal.noHistory': 'Aucun historique de prix enregistré pour l\'instant.',
    'email.verifyNotice': 'Veuillez vérifier votre adresse e-mail pour recevoir les alertes de baisse de prix.',

    // Browser Sync (Feature 035)
    'sync.justNow': 'À l’instant',
    'sync.syncedFromBrowser': 'Prix mis à jour depuis le navigateur !',
    'sync.cooldownActive': 'Déjà synchronisé au cours de la dernière heure.',
    'sync.syncFromBrowser': 'Synchroniser depuis l’onglet',
    'sync.syncing': 'Synchronisation...',

    // User Packages & Retention (Feature 063)
    'packages.title': 'Votre Forfait et Quota',
    'packages.currentPackage': 'Forfait Actuel',
    'packages.activeUsage': '{{active}} sur {{limit}} suivis actifs utilisés',
    'packages.effectiveLimit': 'Limite Effective',
    'packages.activationLimit': 'Limite du Forfait',
    'packages.complimentary': 'Attribution Gratuite',
    'packages.complimentaryDesc': 'Votre forfait est offert gracieusement. Le paiement en ligne arrive bientôt.',
    'packages.paymentsComingSoon': 'Paiements en ligne bientôt disponibles',
    'packages.pendingDowngradeTitle': 'Rétrogradation Planifiée',
    'packages.graceDeadline': 'Fin de la période de grâce : {{deadline}}',
    'packages.graceWarning': 'Vos suivis actifs dépassent la limite {{target}}. Sélectionnez les suivis à conserver ci-dessous avant la fin du délai.',
    'packages.retentionTitle': 'Sélectionner les Suivis à Conserver',
    'packages.retentionSubtitle': 'Choisissez jusqu\'à {{limit}} suivis actifs à maintenir après la rétrogradation.',
    'packages.saveRetention': 'Enregistrer la Sélection',
    'packages.savingRetention': 'Enregistrement...',
    'packages.savedSuccess': 'Sélection de rétention enregistrée.',
    'packages.saveError': 'Échec de l\'enregistrement de la sélection.',
    'packages.oldestFirstNotice': 'Par défaut : les suivis les plus anciens sont conservés en premier sans sélection manuelle.',
    'packages.retainedCount': '{{selected}} / {{limit}} sélectionnés',
    'packages.unretainedPausedNotice': 'Les suivis suspendus conservent tout leur historique de prix.',
    'packages.loading': 'Chargement des détails du forfait...',
    'packages.unavailable': 'Détails du forfait actuellement indisponibles.',
    'packages.retry': 'Réessayer',
    'packages.tierFree': 'GRATUIT',
    'packages.tierPlus': 'PLUS',
    'packages.tierPro': 'PRO',
    'packages.tierSpecial': 'Forfait Spécial',
    'packages.tierPremium': 'Premium',
    'packages.priceMonthly': '{{price}}/mois',
    'packages.quotaExceededAlert': 'Limite de suivis actifs atteinte. Suspendez ou supprimez un autre suivi pour activer celui-ci.',
    'packages.watchesAllowance': '{{limit}} suivis',
    'packages.plannedPricing': 'Forfaits disponibles',
  },

  pt: {
    'plugin.title': "Conectar ao PriceLoss",
    'plugin.loading': "Carregando solicitação de conexão…",
    'plugin.expired': "Esta solicitação de conexão é inválida ou expirou. Volte ao cliente solicitante e comece novamente.",
    'plugin.error': "Não foi possível concluir esta solicitação. Volte ao cliente solicitante e tente novamente.",
    'plugin.complete': "Voltando ao cliente solicitante…",
    'plugin.client': "Cliente solicitante",
    'plugin.account': "Você está conectando esta conta",
    'plugin.permissions': "Permissões solicitadas",
    'plugin.read': "Ler sua conta, produtos acompanhados, histórico de preços e notificações.",
    'plugin.write': "Gerenciar produtos acompanhados e alertas, e verificar produtos.",
    'plugin.events': "Receber eventos de queda de preço da sua conta.",
    'plugin.notice': "Autorize apenas um cliente confiável. Recusar mantém este cliente desconectado.",
    'plugin.submitting': "Salvando sua decisão…",
    'plugin.approve': "Permitir conexão",
    'plugin.deny': "Recusar conexão",
    // Navigation & Header
    'nav.trackTab': 'Monitorar Aba',
    'nav.watchlist': 'Lista de Desejos',
    'nav.notifications': 'Notificações',
    'nav.activeQuota': '{{active}} / {{max}} Ativos',
    'nav.settings': 'Configurações',
    'nav.logout': 'Sair',
    'nav.toggleTheme': 'Alternar tema',
    'nav.openSidePanel': 'Abrir no painel lateral',
    'sidepanel.title': 'Painel lateral PriceLoss',
    'sidepanel.unsupportedPage': 'Abra uma página de produto de comércio eletrônico na sua aba ativa para rastrear.',

    // Notifications
    'notifications.title': 'Alertas de Queda de Preço',
    'notifications.empty': 'Nenhuma notificação ainda',
    'notifications.emptyDesc': 'Quando o preço de um item na sua lista cair, você verá os alertas aqui.',
    'notifications.markAllRead': 'Marcar tudo como lido',
    'notifications.priceDropped': 'Preço caiu em',
    'notifications.savings': 'Economia',
    'notifications.was': 'De',
    'notifications.now': 'Por',

    // Dashboard & Tracking
    'track.readyTitle': 'Pronto para monitorar produtos',
    'track.readyDesc': 'Acesse qualquer produto em uma loja compatível para começar a monitorar o preço.',
    'track.button': 'Monitorar Aba Atual',
    'track.analyzing': 'Analisando detalhes do produto...',
    'track.checking': 'Verificando preço atual...',
    'track.intervalNotice': 'Verificações automáticas a cada 10–30 minutos.',
    'track.unsupported': 'Abra uma página de produto compatível (Amazon, Noon, etc.).',
    'track.standardTier': 'Conta Padrão',
    'track.activeWatches': 'Alertas Ativos',

    // Verification & Alert Setup
    'preview.verified': 'Confirmado',
    'preview.confidence': 'Precisão',
    'preview.alertTitle': 'Configurações de Alerta',
    'preview.alertDesc': 'Avise-me quando o preço cair:',
    'preview.preset.any': 'Qualquer Queda',
    'preview.preset.5': 'Queda de 5%',
    'preview.preset.10': 'Queda de 10%',
    'preview.preset.15': 'Queda de 15%',
    'preview.confirmTrack': 'Começar a Monitorar',
    'preview.creating': 'Criando monitoramento...',
    'preview.alreadyTracking': 'Este produto já está na sua lista.',

    // Active Watch Card
    'watch.active': 'Ativo',
    'watch.activelyMonitored': 'Monitorado',
    'watch.paused': 'Pausado',
    'watch.pause': 'Pausar',
    'watch.resume': 'Retomar',
    'watch.delete': 'Excluir',
    'watch.history': 'Histórico',
    'watch.viewHistory': 'Ver Histórico e Detalhes',
    'watch.confirmDelete': 'Parar de monitorar?',
    'watch.openStore': 'Abrir página da loja',
    'watch.monitoredProduct': 'Produto Monitorado',
    'watch.checkNow': 'Verificar preço agora',
    'watch.checking': 'Verificando...',
    'watch.checkSuccess': 'Preço atualizado',
    'watch.checkNoChange': 'Preço inalterado',
    'watch.cooldownNotice': 'Verificado recentemente. Aguarde {{seconds}}s',
    'watch.checkNotFound': 'Serviço de verificação indisponível neste servidor ou produto não encontrado',
    'watch.checkFailed': 'Falha ao verificar o preço. Tente novamente mais tarde.',
    'watch.hostBusy': 'A loja está ocupada no momento. Tente novamente em alguns instantes.',
    'watch.listPrice': 'Preço Original',
    'watch.discount': 'Desconto',
    'watch.savings': 'Economia Total',

    // Watchlist
    'watchlist.title': 'Produtos Monitorados',
    'watchlist.emptyTitle': 'Nenhum produto monitorado ainda',
    'watchlist.emptyDesc': 'Monitore produtos para receber alertas por email quando o preço cair.',
    'watchlist.filterAll': 'Todos ({{count}})',
    'watchlist.filterActive': 'Ativos ({{count}})',
    'watchlist.filterPaused': 'Pausados ({{count}})',
    'watchlist.searchPlaceholder': 'Buscar produtos...',
    'watchlist.noMatch': 'Nenhum produto corresponde à busca ou filtro.',

    // History & Analytics
    'history.title': 'Histórico de Preços',
    'history.back': 'Voltar à Lista',
    'history.low30': 'Mínima (30d)',
    'history.high30': 'Máxima (30d)',
    'history.latest': 'Preço Atual',
    'history.noData': 'Nenhum histórico registrado ainda.',
    'history.reportIssue': 'Reportar',

    // Report Modal
    'report.title': 'Reportar Problema de Extração',
    'report.actualPrice': 'Preço Observado',
    'report.reason': 'Categoria do Problema',
    'report.notes': 'Observações / Detalhes',
    'report.submit': 'Enviar Relatório',
    'report.cancel': 'Cancelar',
    'report.success': 'Obrigado! Seu feedback foi enviado para análise.',

    // Auth
    'auth.signIn': 'Entrar no PriceLoss',
    'auth.createAccount': 'Criar Conta no PriceLoss',
    'auth.email': 'Email',
    'auth.password': 'Senha',
    'auth.confirmPassword': 'Confirmar Senha',
    'auth.loginBtn': 'Entrar',
    'auth.registerBtn': 'Criar Conta',
    'auth.noAccount': 'Não tem uma conta?',
    'auth.haveAccount': 'Já tem uma conta?',
    'auth.registerLink': 'Criar conta',
    'auth.loginLink': 'Entrar',
    'auth.passwordMismatch': 'As senhas não coincidem.',

    // Email Banner
    'email.required': 'Confirmação necessária',
    'email.desc': 'Confirme seu email para ativar os alertas de preço.',
    'email.banner': 'Confirme seu email para ativar os alertas de preço.',
    'email.resend': 'Reenviar email',
    'email.sent': 'Email enviado!',

    // Settings
    'settings.title': 'Configurações de Desenvolvedor',
    'settings.appearance': 'Aparência',
    'settings.themeDark': 'Tema Escuro',
    'settings.themeLight': 'Tema Claro',
    'settings.themeSystem': 'Padrão do Sistema',
    'settings.language': 'Idioma',
    'settings.apiUrl': 'URL da API',
    'settings.save': 'Salvar Alterações',
    'settings.reset': 'Restaurar',
    'settings.saved': 'Configurações salvas com sucesso!',

    // Cross-Merchant Comparison
    'compare.title': 'Comparar Lojas',
    'compare.bestPrice': 'Melhor Preço',
    'compare.save': 'Economize',
    'compare.otherStores': 'Outras Lojas',
    'compare.noMatches': 'Nenhuma correspondência encontrada em outras lojas ainda',
    'compare.trackThisStore': 'Rastrear esta Loja',
    'compare.tracked': 'Rastreado',
    'compare.outOfStock': 'Esgotado',
    'compare.marketSpread': 'Variação de Preço',
    'compare.searchStores': 'Buscar em outras lojas',
    'compare.searchingStores': 'Buscando nas lojas...',
    'compare.noDiscoveredMatches': 'Nenhuma oferta correspondente encontrada nas lojas concorrentes',

    // Custom Target Price & Alert Thresholds (Feature 029)
    'alert.targetPrice': 'Preço-Alvo',
    'alert.setTargetPrice': 'Definir Preço-Alvo',
    'alert.targetPricePlaceholder': 'ex. 250.00',
    'alert.dropPercentage': '% de Queda no Preço',
    'alert.customPercentage': '% Personalizada',
    'alert.editRule': 'Editar Regra de Alerta',
    'alert.ruleUpdated': 'Preferências de alerta atualizadas',
    'alert.targetAboveCurrent': 'O preço-alvo está acima do preço atual',
    'alert.invalidTargetPrice': 'Insira um preço-alvo válido maior que 0',
    'alert.targetPriceBadge': 'Alvo: {{price}}',

    // Watchlist Filters & Sorting (Feature 029)
    'watchlist.filterStores': 'Lojas',
    'watchlist.allStores': 'Todas as Lojas',
    'watchlist.onSale': 'Em Promoção',
    'watchlist.sortBy': 'Ordenar por',
    'watchlist.sortRecent': 'Verificações recentes',
    'watchlist.sortSavings': 'Maior economia',
    'watchlist.sortLowestPrice': 'Menor preço',
    'watchlist.sortNewest': 'Mais recentes',
    'watchlist.sortOldest': 'Mais antigos',
    'watchlist.clearFilters': 'Limpar filtros',
    'watchlist.noFilterMatches': 'Nenhum produto corresponde aos filtros atuais',

    // Manual Element Picker (Feature 030)
    'picker.selectManually': 'Selecionar manualmente na página',
    'picker.stepPrice': 'Passo 1: Clique no preço na página',
    'picker.stepTitle': 'Passo 2: Clique no título do produto',
    'picker.stepImage': 'Passo 3: Clique na imagem do produto (Opcional)',
    'picker.confirmAndTrack': 'Confirmar e rastrear',
    'picker.reselect': 'Selecionar novamente',
    'picker.cancel': 'Cancelar',
    'picker.skipStep': 'Pular',
    'picker.selectedPrice': 'Preço selecionado',
    'picker.selectedTitle': 'Título selecionado',
    'picker.correctElement': 'Corrigir elemento / Selecionar novamente',
    'picker.saving': 'Salvando...',
    'picker.errorPriceRequired': 'Selecione um elemento de preço válido',
    'picker.connectionError': 'Não foi possível conectar ao inspetor de página. Atualize a página e tente novamente.',
    'picker.refreshPage': 'Atualizar página',

    // Web Platform Enhancements (Feature 032)
    'track.pastePlaceholder': 'Cole o URL de qualquer produto (Amazon, Magalu, eBay...)',
    'track.trackPrice': 'Monitorar Preço',
    'track.tracking': 'Monitorando...',
    'dashboard.searchPlaceholder': 'Buscar produtos ou lojas...',
    'dashboard.filterAll': 'Todos',
    'dashboard.filterActive': 'Ativos',
    'dashboard.filterPaused': 'Pausados',
    'dashboard.emptyTitle': 'Nenhum produto monitorado ainda',
    'dashboard.emptyDesc': 'Cole o URL de um produto acima para começar a monitorar e receber alertas de queda.',
    'dashboard.cardCount': '{{count}} produtos',
    'card.pauseTracking': 'Pausar monitoramento',
    'card.resumeTracking': 'Retomar monitoramento',
    'card.viewDetails': 'Ver detalhes e histórico',
    'card.visitStore': 'Visitar loja',
    'card.delete': 'Excluir monitoramento',
    'card.statusActive': 'ATIVO',
    'card.statusPaused': 'PAUSADO',
    'card.currentPrice': 'Preço Atual',
    'card.listPrice': 'Preço Original',
    'card.save': 'Economia',
    'dialog.deleteTitle': 'Excluir Monitoramento',
    'dialog.deleteDesc': 'Tem certeza de que deseja parar de monitorar "{{title}}"? Novas observações de preço não serão registradas.',
    'dialog.cancel': 'Cancelar',
    'dialog.confirmDelete': 'Excluir',
    'modal.productDetails': 'Detalhes do Produto',
    'modal.priceHistory': 'Histórico e Análise de Preços',
    'modal.alertSettings': 'Configurações de Alerta',
    'modal.range30d': '30 Dias',
    'modal.range90d': '90 Dias',
    'modal.rangeAll': 'Todo o Histórico',
    'modal.targetPrice': 'Preço Alvo',
    'modal.dropPercentage': '% de Queda',
    'modal.saveAlert': 'Salvar Alerta',
    'modal.saving': 'Salvando...',
    'modal.saveSuccess': 'Regra de alerta atualizada com sucesso',
    'modal.anyDrop': 'Qualquer queda',
    'modal.drop5': '5% de queda',
    'modal.drop10': '10% de queda',
    'modal.drop15': '15% de queda',
    'modal.drop20': '20% de queda',
    'modal.drop25': '25% de queda',
    'modal.customDrop': '% Personalizado',
    'modal.storeLink': 'Abrir Loja',
    'modal.currentPrice': 'Preço Atual',
    'modal.originalPrice': 'Preço Original',
    'modal.savings': 'Valor Economizado',
    'modal.noHistory': 'Nenhum dado histórico registrado ainda.',
    'email.verifyNotice': 'Verifique seu e-mail para garantir o recebimento de alertas de queda de preço.',

    // Browser Sync (Feature 035)
    'sync.justNow': 'Agora mesmo',
    'sync.syncedFromBrowser': 'Preço atualizado pelo navegador!',
    'sync.cooldownActive': 'Já sincronizado na última hora.',
    'sync.syncFromBrowser': 'Sincronizar da aba do navegador',
    'sync.syncing': 'Sincronizando...',

    // User Packages & Retention (Feature 063)
    'packages.title': 'Seu Pacote e Limite de Monitoramento',
    'packages.currentPackage': 'Plano Atual',
    'packages.activeUsage': '{{active}} de {{limit}} monitoramentos ativos usados',
    'packages.effectiveLimit': 'Limite Efetivo',
    'packages.activationLimit': 'Limite do Pacote',
    'packages.complimentary': 'Concessão Cortesia',
    'packages.complimentaryDesc': 'Seu pacote foi concedido como cortesia. Pagamentos online em breve.',
    'packages.paymentsComingSoon': 'Pagamentos online em breve',
    'packages.pendingDowngradeTitle': 'Rebaixamento de Plano Agendado',
    'packages.graceDeadline': 'Fim do período de tolerância: {{deadline}}',
    'packages.graceWarning': 'Seus monitoramentos ativos excedem o limite de {{target}}. Selecione quais deseja reter abaixo antes do prazo.',
    'packages.retentionTitle': 'Selecionar Monitoramentos para Reter',
    'packages.retentionSubtitle': 'Escolha até {{limit}} monitoramentos ativos para manter após o rebaixamento.',
    'packages.saveRetention': 'Salvar Monitoramentos Retidos',
    'packages.savingRetention': 'Salvando seleção...',
    'packages.savedSuccess': 'Seleção de retenção salva com sucesso.',
    'packages.saveError': 'Falha ao salvar seleção de retenção.',
    'packages.oldestFirstNotice': 'Padrão: monitoramentos mais antigos são retidos primeiro sem seleção manual.',
    'packages.retainedCount': '{{selected}} / {{limit}} selecionados',
    'packages.unretainedPausedNotice': 'Monitoramentos pausados preservam todo o histórico de preços.',
    'packages.loading': 'Carregando detalhes do pacote...',
    'packages.unavailable': 'Detalhes do pacote indisponíveis no momento.',
    'packages.retry': 'Tentar novamente',
    'packages.tierFree': 'GRÁTIS',
    'packages.tierPlus': 'PLUS',
    'packages.tierPro': 'PRO',
    'packages.tierSpecial': 'Pacote Especial',
    'packages.tierPremium': 'Premium',
    'packages.priceMonthly': '{{price}}/mês',
    'packages.quotaExceededAlert': 'Limite de monitoramentos ativos atingido. Pause ou exclua outro monitoramento para ativar este.',
    'packages.watchesAllowance': '{{limit}} monitoramentos',
    'packages.plannedPricing': 'Planos disponíveis',
  },

  es: {
    'plugin.title': "Conectar a PriceLoss",
    'plugin.loading': "Cargando solicitud de conexión…",
    'plugin.expired': "Esta solicitud de conexión no es válida o ha caducado. Vuelve al cliente solicitante y empieza de nuevo.",
    'plugin.error': "No se pudo completar esta solicitud. Vuelve al cliente solicitante e inténtalo de nuevo.",
    'plugin.complete': "Volviendo al cliente solicitante…",
    'plugin.client': "Cliente solicitante",
    'plugin.account': "Estás conectando esta cuenta",
    'plugin.permissions': "Permisos solicitados",
    'plugin.read': "Leer tu cuenta, productos seguidos, historial de precios y notificaciones.",
    'plugin.write': "Gestionar productos seguidos y alertas, y verificar productos.",
    'plugin.events': "Recibir eventos de bajada de precio de tu cuenta.",
    'plugin.notice': "Autoriza solo a un cliente de confianza. Rechazar mantiene este cliente desconectado.",
    'plugin.submitting': "Guardando tu decisión…",
    'plugin.approve': "Permitir conexión",
    'plugin.deny': "Rechazar conexión",
    // Navigation & Header
    'nav.trackTab': 'Seguir Pestaña',
    'nav.watchlist': 'Seguimientos',
    'nav.notifications': 'Notificaciones',
    'nav.activeQuota': '{{active}} / {{max}} Activos',
    'nav.settings': 'Ajustes',
    'nav.logout': 'Cerrar sesión',
    'nav.toggleTheme': 'Cambiar tema',
    'nav.openSidePanel': 'Abrir en el panel lateral',
    'sidepanel.title': 'Panel lateral PriceLoss',
    'sidepanel.unsupportedPage': 'Abre una página de producto de comercio electrónico en tu pestaña activa para rastrear.',

    // Notifications
    'notifications.title': 'Alertas de bajada de precio',
    'notifications.empty': 'No hay notificaciones aún',
    'notifications.emptyDesc': 'Cuando baje el precio de un artículo en tu lista, verás alertas aquí.',
    'notifications.markAllRead': 'Marcar todo como leído',
    'notifications.priceDropped': 'El precio bajó un',
    'notifications.savings': 'Ahorro',
    'notifications.was': 'Antes',
    'notifications.now': 'Ahora',

    // Dashboard & Tracking
    'track.readyTitle': 'Listo para rastrear productos',
    'track.readyDesc': 'Navega a la página de cualquier producto en una tienda compatible para monitorear su precio.',
    'track.button': 'Monitorear Pestaña Actual',
    'track.analyzing': 'Escaneando detalles del producto...',
    'track.checking': 'Verificando precio actual...',
    'track.intervalNotice': 'Comprobaciones automáticas cada 10–30 minutos.',
    'track.unsupported': 'Abre la página de un producto en una tienda compatible (Amazon, Noon, etc.).',
    'track.standardTier': 'Cuenta Estándar',
    'track.activeWatches': 'Alertas Activas',

    // Verification & Alert Setup
    'preview.verified': 'Verificado',
    'preview.confidence': 'Confianza',
    'preview.alertTitle': 'Configuración de Alertas',
    'preview.alertDesc': 'Notificarme si baja de precio:',
    'preview.preset.any': 'Cualquier bajada',
    'preview.preset.5': 'Bajada 5%',
    'preview.preset.10': 'Bajada 10%',
    'preview.preset.15': 'Bajada 15%',
    'preview.confirmTrack': 'Empezar a Rastrear',
    'preview.creating': 'Creando alerta...',
    'preview.alreadyTracking': 'Este producto ya está en tu lista de seguimiento.',

    // Active Watch Card
    'watch.active': 'Activo',
    'watch.activelyMonitored': 'Monitoreado',
    'watch.paused': 'Pausado',
    'watch.pause': 'Pausar',
    'watch.resume': 'Reanudar',
    'watch.delete': 'Eliminar',
    'watch.history': 'Historial',
    'watch.viewHistory': 'Ver Historial y Detalles',
    'watch.confirmDelete': '¿Dejar de rastrear?',
    'watch.openStore': 'Abrir en la tienda',
    'watch.monitoredProduct': 'Producto Monitoreado',
    'watch.checkNow': 'Comprobar precio ahora',
    'watch.checking': 'Comprobando...',
    'watch.checkSuccess': 'Precio actualizado',
    'watch.checkNoChange': 'Precio sin cambios',
    'watch.cooldownNotice': 'Comprobado recientemente. Espera {{seconds}}s',
    'watch.checkNotFound': 'Servicio de verificación no disponible en este servidor o producto no encontrado',
    'watch.checkFailed': 'Error al verificar el precio. Inténtalo de nuevo más tarde.',
    'watch.hostBusy': 'La tienda está ocupada en este momento. Inténtalo de nuevo en unos instantes.',
    'watch.listPrice': 'Precio Original',
    'watch.discount': 'Descuento',
    'watch.savings': 'Ahorro Total',

    // Watchlist
    'watchlist.title': 'Productos en Seguimiento',
    'watchlist.emptyTitle': 'No hay productos en seguimiento aún',
    'watchlist.emptyDesc': 'Rastrea productos para recibir alertas por email cuando baje su precio.',
    'watchlist.filterAll': 'Todos ({{count}})',
    'watchlist.filterActive': 'Activos ({{count}})',
    'watchlist.filterPaused': 'Pausados ({{count}})',
    'watchlist.searchPlaceholder': 'Buscar productos...',
    'watchlist.noMatch': 'Ningún producto coincide con el filtro.',

    // History & Analytics
    'history.title': 'Historial de Precios',
    'history.back': 'Volver al Seguimiento',
    'history.low30': 'Mínimo (30d)',
    'history.high30': 'Máximo (30d)',
    'history.latest': 'Precio Actual',
    'history.noData': 'No hay historial de precios registrado aún.',
    'history.reportIssue': 'Reportar',

    // Report Modal
    'report.title': 'Reportar Problema de Extracción',
    'report.actualPrice': 'Precio Observado',
    'report.reason': 'Categoría del Problema',
    'report.notes': 'Notas / Detalles',
    'report.submit': 'Enviar Reporte',
    'report.cancel': 'Cancelar',
    'report.success': '¡Gracias! Tu reporte ha sido enviado para calibración.',

    // Auth
    'auth.signIn': 'Iniciar Sesión en PriceLoss',
    'auth.createAccount': 'Crear Cuenta en PriceLoss',
    'auth.email': 'Correo Electrónico',
    'auth.password': 'Contraseña',
    'auth.confirmPassword': 'Confirmar Contraseña',
    'auth.loginBtn': 'Iniciar Sesión',
    'auth.registerBtn': 'Registrarse',
    'auth.noAccount': '¿No tienes cuenta?',
    'auth.haveAccount': '¿Ya tienes una cuenta?',
    'auth.registerLink': 'Crear una',
    'auth.loginLink': 'Iniciar Sesión',
    'auth.passwordMismatch': 'Las contraseñas no coinciden.',

    // Email Banner
    'email.required': 'Verificación requerida',
    'email.desc': 'Revisa tu correo para verificar tu cuenta y activar las alertas.',
    'email.banner': 'Revisa tu correo para verificar tu cuenta y activar las alertas.',
    'email.resend': 'Reenviar correo',
    'email.sent': '¡Correo enviado!',

    // Settings
    'settings.title': 'Ajustes de Desarrollador',
    'settings.appearance': 'Apariencia',
    'settings.themeDark': 'Tema Oscuro',
    'settings.themeLight': 'Tema Claro',
    'settings.themeSystem': 'Tema del Sistema',
    'settings.language': 'Idioma',
    'settings.apiUrl': 'URL de la API',
    'settings.save': 'Guardar Cambios',
    'settings.reset': 'Restablecer',
    'settings.saved': '¡Ajustes guardados con éxito!',

    // Cross-Merchant Comparison
    'compare.title': 'Comparar Tiendas',
    'compare.bestPrice': 'Mejor Precio',
    'compare.save': 'Ahorra',
    'compare.otherStores': 'Otras Tiendas',
    'compare.noMatches': 'No se encontraron coincidencias en otras tiendas todavía',
    'compare.trackThisStore': 'Seguir esta Tienda',
    'compare.tracked': 'Seguido',
    'compare.outOfStock': 'Agotado',
    'compare.marketSpread': 'Diferencia de Precio',
    'compare.searchStores': 'Buscar en otras tiendas',
    'compare.searchingStores': 'Buscando en las tiendas...',
    'compare.noDiscoveredMatches': 'No se encontraron ofertas coincidentes en tiendas competidoras',

    // Custom Target Price & Alert Thresholds (Feature 029)
    'alert.targetPrice': 'Precio Objetivo',
    'alert.setTargetPrice': 'Fijar Precio Objetivo',
    'alert.targetPricePlaceholder': 'ej. 250.00',
    'alert.dropPercentage': '% de Caída de Precio',
    'alert.customPercentage': '% Personalizado',
    'alert.editRule': 'Editar Regra de Alerta',
    'alert.ruleUpdated': 'Preferencias de alerta actualizadas',
    'alert.targetAboveCurrent': 'El precio objetivo supera el precio actual',
    'alert.invalidTargetPrice': 'Introduce un precio objetivo válido mayor que 0',
    'alert.targetPriceBadge': 'Objetivo: {{price}}',

    // Watchlist Filters & Sorting (Feature 029)
    'watchlist.filterStores': 'Tiendas',
    'watchlist.allStores': 'Todas las Tiendas',
    'watchlist.onSale': 'En Oferta',
    'watchlist.sortBy': 'Ordenar por',
    'watchlist.sortRecent': 'Comprobación reciente',
    'watchlist.sortSavings': 'Mayor ahorro',
    'watchlist.sortLowestPrice': 'Precio más bajo',
    'watchlist.sortNewest': 'Añadidos recientemente',
    'watchlist.sortOldest': 'Añadidos anteriormente',
    'watchlist.clearFilters': 'Borrar filtros',
    'watchlist.noFilterMatches': 'Ningún producto coincide con los filtros actuales',

    // Manual Element Picker (Feature 030)
    'picker.selectManually': 'Seleccionar manualmente en la página',
    'picker.stepPrice': 'Paso 1: Haz clic en el precio en la página',
    'picker.stepTitle': 'Paso 2: Haz clic en el título del producto',
    'picker.stepImage': 'Paso 3: Haz clic en la imagen del producto (Opcional)',
    'picker.confirmAndTrack': 'Confirmar y rastrear',
    'picker.reselect': 'Volver a seleccionar',
    'picker.cancel': 'Cancelar',
    'picker.skipStep': 'Omitir',
    'picker.selectedPrice': 'Precio seleccionado',
    'picker.selectedTitle': 'Título seleccionado',
    'picker.correctElement': 'Corregir elemento / Volver a seleccionar',
    'picker.saving': 'Guardando...',
    'picker.errorPriceRequired': 'Selecciona un elemento de precio válido',
    'picker.connectionError': 'No se pudo conectar con el inspector de página. Por favor recarga la página e inténtalo de nuevo.',
    'picker.refreshPage': 'Recargar página',

    // Web Platform Enhancements (Feature 032)
    'track.pastePlaceholder': 'Pega la URL de cualquier producto (Amazon, MercadoLibre, eBay...)',
    'track.trackPrice': 'Rastrear Precio',
    'track.tracking': 'Rastreando...',
    'dashboard.searchPlaceholder': 'Buscar productos o tiendas...',
    'dashboard.filterAll': 'Todos',
    'dashboard.filterActive': 'Activos',
    'dashboard.filterPaused': 'En pausa',
    'dashboard.emptyTitle': 'Aún no tienes productos rastreados',
    'dashboard.emptyDesc': 'Pega la URL de un producto arriba para monitorear precios y recibir alertas de bajada.',
    'dashboard.cardCount': '{{count}} productos',
    'card.pauseTracking': 'Pausar seguimiento',
    'card.resumeTracking': 'Reanudar seguimiento',
    'card.viewDetails': 'Ver detalles e historial',
    'card.visitStore': 'Visitar tienda',
    'card.delete': 'Eliminar seguimiento',
    'card.statusActive': 'ACTIVO',
    'card.statusPaused': 'EN PAUSA',
    'card.currentPrice': 'Precio Actual',
    'card.listPrice': 'Precio Original',
    'card.save': 'Ahorro',
    'dialog.deleteTitle': 'Eliminar Seguimiento',
    'dialog.deleteDesc': '¿Seguro que deseas dejar de rastrear "{{title}}"? Ya no se registrarán bajadas de precio para este producto.',
    'dialog.cancel': 'Cancelar',
    'dialog.confirmDelete': 'Eliminar',
    'modal.productDetails': 'Detalles del Producto',
    'modal.priceHistory': 'Historial y Análisis de Precios',
    'modal.alertSettings': 'Configuración de Alerta',
    'modal.range30d': '30 Días',
    'modal.range90d': '90 Días',
    'modal.rangeAll': 'Todo el Historial',
    'modal.targetPrice': 'Precio Objetivo',
    'modal.dropPercentage': '% de Caída',
    'modal.saveAlert': 'Guardar Alerta',
    'modal.saving': 'Guardando...',
    'modal.saveSuccess': 'Regra de alerta actualizada con éxito',
    'modal.anyDrop': 'Cualquier caída',
    'modal.drop5': '5% de caída',
    'modal.drop10': '10% de caída',
    'modal.drop15': '15% de caída',
    'modal.drop20': '20% de caída',
    'modal.drop25': '25% de caída',
    'modal.customDrop': '% Personalizado',
    'modal.storeLink': 'Abrir Tienda',
    'modal.currentPrice': 'Precio Actual',
    'modal.originalPrice': 'Precio Original',
    'modal.savings': 'Ahorro Obtenido',
    'modal.noHistory': 'Aún no hay datos históricos registrados.',
    'email.verifyNotice': 'Verifica tu correo electrónico para asegurarte de recibir las alertas de caída de precio.',

    // Browser Sync (Feature 035)
    'sync.justNow': 'Hace un momento',
    'sync.syncedFromBrowser': '¡Precio actualizado desde el navegador!',
    'sync.cooldownActive': 'Ya se ha sincronizado en la última hora.',
    'sync.syncFromBrowser': 'Sincronizar desde pestaña del navegador',
    'sync.syncing': 'Sincronizando...',

    // User Packages & Retention (Feature 063)
    'packages.title': 'Tu Paquete y Límite de Seguimientos',
    'packages.currentPackage': 'Plan Actual',
    'packages.activeUsage': '{{active}} de {{limit}} seguimientos activos usados',
    'packages.effectiveLimit': 'Límite Efectivo',
    'packages.activationLimit': 'Límite del Paquete',
    'packages.complimentary': 'Concesión de Cortesía',
    'packages.complimentaryDesc': 'Tu paquete se otorga como cortesía. Los pagos en línea estarán disponibles pronto.',
    'packages.paymentsComingSoon': 'Pagos en línea próximamente',
    'packages.pendingDowngradeTitle': 'Reducción de Plan Programada',
    'packages.graceDeadline': 'El período de gracia termina: {{deadline}}',
    'packages.graceWarning': 'Tus seguimientos activos superan el límite de {{target}}. Selecciona cuáles conservar abajo antes de que termine el plazo.',
    'packages.retentionTitle': 'Seleccionar Seguimientos a Conservar',
    'packages.retentionSubtitle': 'Elige hasta {{limit}} seguimientos activos para mantener tras la reducción.',
    'packages.saveRetention': 'Guardar Seguimientos Conservados',
    'packages.savingRetention': 'Guardando selección...',
    'packages.savedSuccess': 'Selección de conservación guardada con éxito.',
    'packages.saveError': 'Error al guardar la selección.',
    'packages.oldestFirstNotice': 'Por defecto: se conservan primero los seguimientos más antiguos sin selección manual.',
    'packages.retainedCount': '{{selected}} / {{limit}} seleccionados',
    'packages.unretainedPausedNotice': 'Los seguimientos pausados conservan todo su historial de precios.',
    'packages.loading': 'Cargando detalles del paquete...',
    'packages.unavailable': 'Detalles del paquete no disponibles actualmente.',
    'packages.retry': 'Reintentar',
    'packages.tierFree': 'GRATIS',
    'packages.tierPlus': 'PLUS',
    'packages.tierPro': 'PRO',
    'packages.tierSpecial': 'Paquete Especial',
    'packages.tierPremium': 'Premium',
    'packages.priceMonthly': '{{price}}/mes',
    'packages.quotaExceededAlert': 'Límite de seguimientos activos alcanzado. Pausa o elimina otro seguimiento para activar este.',
    'packages.watchesAllowance': '{{limit}} seguimientos',
    'packages.plannedPricing': 'Planes disponibles',
  },
};

export type TranslationKey = keyof typeof translations.en;

export function formatTranslation(
  text: string,
  params?: Record<string, string | number>
): string {
  if (!params) return text;
  return Object.entries(params).reduce((acc, [k, v]) => {
    return acc.replaceAll(`{{${k}}}`, String(v));
  }, text);
}
